<template>
  <div
    v-if="questions"
    class="c-guided-quiz"
  >
    <guided-quiz-progress-bar
      :current-step="currentQuestionNumber"
      :total-steps="questions.length"
    />
    <div
      v-if="currentQuestion !== null"
      class="c-guided-quiz__header l-grid l-grid--3-col"
    >
      <div>
        <GuidedQuizBackButton
          v-if="showBackBtn"
          @back="back"
        />
      </div>
      <div class="c-guided-quiz__location t-heading-8 u-text-align-center">
        Question {{ currentQuestionNumber }}/{{ questions.length }}
      </div>
    </div>
    <guided-quiz-question
      aria-live="polite"
      :question="currentQuestion"
      :display-alt="displayAlternates"
      @answer="answerChosen"
    />
    <div
      v-if="showNextButton"
      class="c-guided-quiz__next"
    >
      <btn
        @click="nextQuestion"
      >
        Next
      </btn>
    </div>
    <div
      v-if="showResults"
      aria-live="polite"
    >
      <div class="c-guided-quiz__header">
        <div class="l-container">
          <p class="t-heading-10 t-heading-10--light u-text-align-center u-margin-bottom-medium">
            Take a look
          </p>
          <h1 class="t-heading-2 u-margin-bottom-small u-text-align-center">
            {{ title }}
          </h1>
          <p class="t-paragraph-large c-guided-quiz__header-subtitle">
            {{ subtitle }}
          </p>
        </div>
      </div>
      <product-grid
        :products="filteredResults"
        :filtered="true"
        :truncate="false"
        :max-products="filteredResults.length"
      />
      <div class="c-guided-quiz__retake">
        <div class="l-container">
          <h3 class="t-heading-4 u-margin-bottom-medium u-text-align-center">
            {{ retakeTitle }}
          </h3>
          <div class="c-guided-quiz__retake-flex">
            <GuidedQuizBackButton
              label="Retake the Quiz"
              :start-over="true"
              @back="startOver"
            />
            <p
              v-if="retakeCta"
              class="t-heading-8 c-guided-quiz__retake-or"
            >
              &mdash;or&mdash;
            </p>
            <btn
              v-if="retakeCta"
              v-bind="retakeCta"
            >
              {{ retakeLabel }}
            </btn>
          </div>
        </div>
      </div>
      <slot />
    </div>
  </div>
</template>

<script>
import get from 'lodash/get'
import GuidedQuizProgressBar from "./GuidedQuizProgressBar";
import GuidedQuizBackButton from "./GuidedQuizBackButton";
import GuidedQuizQuestion from "./GuidedQuizQuestion";
import ProductGrid from "@/components/productGrid/ProductGrid";
import Btn from "../button/Btn";
export default {
  name: "GuidedQuiz",
  components:{
    Btn,
    GuidedQuizProgressBar,
    GuidedQuizBackButton,
    GuidedQuizQuestion,
    ProductGrid,
  },
  props: {
    questions: {
      type: Array,
      default: () => null
    },
    results: {
      type: Array,
      default: null
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    retakeTitle: {
      type: String,
      default: '',
    },
    retakeCta: {
      type: Object,
      default: () => ({}),
    },
    favoritesTitle: {
      type: String,
      default: '',
    },
    favoritesSubtitle: {
      type: String,
      default: '',
    },
    favoritesCta: {
      type: Object,
      default: () => ({}),
    },
    mappedSections: {
      type: Array,
      default: () => []
    }
  },
  data(){
    return {
      currentQuestionIndex: 0,
      selectedAnswers:[],
      displayAlternates: false,
      resultCollections: [],
      filters:[],
      answers: [],
      priorityAnswers: [],
      priorityPrice: '',
      priceDivider: 24.99,
      priorityColors: [],
      filteredResults: [],
    }
  },
  computed:{
    retakeLabel() {
      const cta = this.retakeCta
      return get(cta, 'text')
    },
    showBackBtn() {
      const currentQuestionIndex = this.currentQuestionIndex
      const showResults = this.showResults
      return currentQuestionIndex > 0 && !showResults
    },
    currentQuestion() {
      const questions = this.questions
      const currentQuestionIndex = this.currentQuestionIndex
      if (questions && (currentQuestionIndex >= questions.length)) {
        return null
      }
      return questions[currentQuestionIndex]
    },
    currentQuestionNumber(){
      return this.currentQuestionIndex + 1
    },
    showNextButton(){
      const currentQuestion = this.currentQuestion
      const selectedAnswers = this.selectedAnswers
      const currentQuestionIndex = this.currentQuestionIndex
      return (
        currentQuestion
        && currentQuestion.multipleAnswers
        && selectedAnswers[currentQuestionIndex]
        && ((selectedAnswers[currentQuestionIndex].collectionGroups
        && selectedAnswers[currentQuestionIndex].collectionGroups.length > 0)
          || (selectedAnswers[currentQuestionIndex].filters
            && selectedAnswers[currentQuestionIndex].filters.length > 0))
      )
    },
    showResults() {
      const currentQuestionIndex = this.currentQuestionIndex
      const questions = this.questions
      return questions && (currentQuestionIndex >= questions.length)
    },
  },
  watch: {
    questions() {
      this.prefetchImages()
    },
  },
  mounted() {
    this.prefetchImages()
    this.filteredResults = this.results
  },
  methods: {
    prefetchImages() {
      let images = [];
      if (this.questions && this.questions.length) {
        for (let i = 0; i < this.questions.length; ++i){
          for (let j = 0; j < this.questions[i].answers.length; ++j){
            let ans = this.questions[i].answers[j];
            if (ans.image) {
              let img = new Image();
              img.src = ans.image.src;
              images.push(img);
            }
            if (ans.altImage) {
              let img = new Image();
              img.src = ans.altImage.src;
              images.push(img);
            }
          }
        }
      }
    },
    answerChosen(answer){
      answer.title = answer.title.replace(/\s+/g, '-').toLowerCase();
      // if the questions can only have one answer
      if(!this.currentQuestion.multipleAnswers){
        if (answer.prioritizedAnswer) {
          if(answer.type) {
            if(answer.type.toLowerCase() == "price") {
              this.priorityPrice = answer.title;
            } else {
              this.priorityAnswers.push(answer.title)
            }
          } else {
            this.priorityAnswers.push(answer.title)
          }
        } else if(!answer.skipAnswer) {
          this.answers.push(answer.title);
        }

        // this.nextQuestion();
      } else { // if the question can have multiple answers
        //if the selected answer has already been selected than remove it from selected answers
        if(this.answers.includes(answer.title)) {
          this.answers.splice(this.answers.indexOf(answer.title), 1);
        } else if (!this.answers.prioritizedAnswer && answer.skipAnswer) {
          // if the selected answer has not already been selected than add it to selected answers
          this.answers.push(answer.title);
        } else if (this.priorityColors.includes(answer.title)) {
          this.priorityColors.splice(this.answers.indexOf(answer.title), 1);
        } else {
          this.priorityColors.push(answer.title);
        }
      }
      if(!this.currentQuestion.multipleAnswers){
        this.selectedAnswers.push({
          handle: this.currentQuestion.handle,
          points: this.currentQuestion.points,
          filter: answer.filter,
          collections: answer.collections,
          displayAlts: answer.displayAlts
        });
        this.nextQuestion();
      } else{
        if(!this.selectedAnswers[this.currentQuestionIndex]){
          this.selectedAnswers.push({
            handle: this.currentQuestion.handle,
            points: this.currentQuestion.points,
            filters: [answer.filter],
            collectionGroups: [answer.collections],
            displayAlts: false
          });
        } else {
          let existingIndex = this.selectedAnswers[this.currentQuestionIndex].filters.indexOf(answer.filters);
          if(existingIndex > -1){
            this.selectedAnswers[this.currentQuestionIndex].collectionGroups.splice(existingIndex, 1);
            this.selectedAnswers[this.currentQuestionIndex].filters.splice(existingIndex, 1);
          } else {
            this.selectedAnswers[this.currentQuestionIndex].collectionGroups.push(answer.collections);
            this.selectedAnswers[this.currentQuestionIndex].filters.push(answer.filter);
          }
        }
      }
    },
    nextQuestion(){
      this.currentQuestionIndex++;
      this.updateAlternates();
      if(this.showResults){
        this.processAnswers();
        this.resultCollections.sort((a, b) => (a.points < b.points) ? 1 : -1);
        //clean out anything below 3
        for(let i = this.resultCollections.length-1; i > 0; --i){
          if(this.resultCollections[i].points < 3){
            this.resultCollections.splice(i, 1);
          }
        }
        this.$emit("results", {
          resultCollections:this.resultCollections,
          filters: this.filters
        })
      }
      this.$forceUpdate();
    },
    updateAlternates(){
      let alts = false;
      for(let i = 0; i < this.selectedAnswers.length;++i){
        if(this.selectedAnswers[i].displayAlts){
          alts = true;
        }
      }
      this.displayAlternates = alts;
    },
    back(){
      this.selectedAnswers.pop();
      this.currentQuestionIndex--;
      this.$forceUpdate();
    },
    startOver(){
      this.filters = [];
      this.resultCollections = [];
      this.selectedAnswers = [];
      this.displayAlternates = false;
      this.currentQuestionIndex = 0;

      // reset answers and results
      this.filteredResults = [];
      this.answers = [];
      this.priorityAnswers = [];
      this.priorityColors = [];
    },
    processAnswers(){
      // Apply Score
      for(let i = 0; i < this.results.length;++i) {
        let score = 0;
        let priorityTest = true;
        let priceTest = false;
        let colorTest = false
        let guidedQuiz;

        if (this.results[i].metafieldsMap.guided_quiz) {
          guidedQuiz = this.results[i].metafieldsMap.guided_quiz;
        }



        if (this.priorityPrice == "love-to-save-money") {
          if (this.results[i].minPrice <= this.priceDivider) {
            priceTest = true;
          }
        } else {
          if (this.results[i].minPrice > this.priceDivider) {
            priceTest = true;
          }
        }
        for(let j = 0; j < this.priorityAnswers.length;++j) {
          let priorityAnswer = this.priorityAnswers[j];
          if(guidedQuiz) {
            if (!guidedQuiz.value.includes(priorityAnswer)) {
              priorityTest = false;
            }
          }
        }
        for(let j = 0; j < this.priorityColors.length;++j) {
          let priorityColor = this.priorityColors[j];
          if(guidedQuiz) {
            if (guidedQuiz.value.includes(priorityColor)) {
              colorTest = true;
            }
          }
        }

        if (priorityTest && colorTest && priceTest) {
          this.results[i].priorityTest = true;
        } else {
          this.results[i].priorityTest = false;
        }

        for(let j = 0; j < this.answers.length;++j) {
          let answer = this.answers[j];
          if (guidedQuiz) {
            let guidedQuiz = this.results[i].metafieldsMap.guided_quiz;
            if (guidedQuiz.value.includes(answer)) {
              score++;
            }
          }
        }
        this.results[i].score = score;
      }
      // Order Products based on scores
      let handleTracker = [];
      const maxAmount = 12;
      let amountTracker = 0;
      this.filteredResults = []
      for(let i = this.answers.length; i >= 0; --i) {
        if (amountTracker < maxAmount) {
          for(let j = 0; j < this.results.length; ++j) {
            if (this.results[j].score == i && this.results[j].priorityTest) {
              if (!handleTracker.includes(this.results[j].handle)) {
                if (amountTracker < maxAmount) {
                  this.filteredResults.push(this.results[j]);
                  handleTracker.push(this.results[j].handle);
                  amountTracker++;
                } else {
                  break
                }
              }
            }
          }
        } else {
          break
        }
      }

      this.filters = [];
      for(let i = 0; i < this.selectedAnswers.length;++i) {
        let ans = this.selectedAnswers[i];
        //get filters from th answers
        if (ans.filter || ans.filters) {
          if (ans.handle !== "" && ans.handle !== null) {
            if (ans.filter && ans.filter !== "" && ans.filter !== null) {
              this.filters.push({
                handle: ans.handle,
                filter: ans.filter
              });
            } else if (ans.filters && ans.filters.length > 0) {
              this.filters.push({
                handle: ans.handle,
                filters: ans.filters
              });
            }
          }
        }
        //get and rate the collections from the answers
        if (ans.collections && ans.collections !== "" && ans.points){
          let collections = ans.collections;
          for(let j = 0; j < collections.length;++j){
            let col = collections[j];
            this.appendResult(col, ans.points);
          }
        }
        //get and rate the collections from the multiple selections
        //WARNING: initial data set did not use this feature. Untested.
        if(ans.collectionGroups && ans.collectionGroups.length > 0){
          for(let j = 0; j < ans.collectionGroups.length;++j){
            let collections = ans.collectionGroups[j] ? ans.collectionGroups[j]:[];
            for(let k = 0; k < collections.length;++k){
              let col = collections[k];
              this.appendResult(col, ans.points);
            }
          }
        }
      }
    },
    appendResult(collection, points){
      let found = false;
      for(let i = 0; i < this.resultCollections.length;++i){
        if(this.resultCollections[i].name == collection){
          this.resultCollections[i].points += points;
          found = true;
        }
      }
      if(!found){
        this.resultCollections.push({
          name: collection,
          points: points
        });
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .c-guided-quiz{
    &__location{
      @include themify($themes){
        color: themed("foreground", "light-text");
      }
    }

    &__next{
      text-align: center;
    }

    &__header {
      padding: rem(20px);
    }
    &__header-subtitle {
      max-width: rem(400px);
      text-align: center;
      margin: auto;
    }
    &__retake {
      padding: $section-padding;
      @include themify($themes){
        background-color: themed("background", "xx-light");
      }
    }
    &__retake-or {
      padding: rem(20px);
    }
    &__retake-flex {
      display: flex;
      justify-content: center;

      @include respond(tab-port) {
        display: block;
        text-align: center;
      }
    }
    &__favorites {
      padding: $section-padding;
    }
    &__favorites-subtitle {
      max-width: rem(350px);
    }
  }
</style>
